import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

import { scheduleApi } from "@shared/apis"
import { PaymentInformation, useAccount, useSchedule } from "@shared/contexts"

import {
  hasReachedMaxPrice,
  WowMoneyNotification,
  WowMoneyPopupType,
  WowMoneyStatus,
} from "live/constants"
import { useWowMoney } from "live/hooks"

const FREE_PAYMENT_INFORMATIONS: PaymentInformation[] = ["ALL", "FREE"]

export interface WowMoneyState {
  /** 현재 적립 주기의 시작 시각. 이 시각 + 1시간에 적립된다 */
  cycleStartedAt?: number
  /** 이번 달 누적 와우머니. 조회 전에는 undefined */
  cumulativePrice?: number
  /** 상단바 적립 현황이 보여줄 상태 */
  status: WowMoneyStatus
  /**
   * 입장 시점 기준 적립 대상 여부(월 한도를 이미 채웠으면 false).
   * status 와 판정 규칙은 같지만 값을 공유하지 않는다 - 이 값이
   * 실시간으로 뒤집히면 완료 축하 팝업이 뜨자마자 사라진다
   */
  isAccrualTarget: boolean
  /** 편성·방송 상태상 적립 기능이 동작할 수 있는지 */
  isAvailable: boolean
  /** 유료 편성이 아닌지. 방송 종료 여부는 보지 않는다 */
  isFreeSchedule: boolean
  /** 적립 완료 · 오류 등 헤더에 알릴 내용 */
  notification?: WowMoneyNotification
  clearNotification: () => void
  popupType?: WowMoneyPopupType
  setPopupType: (type?: WowMoneyPopupType) => void
}

const wowMoneyContext = createContext<WowMoneyState>({
  status: "ACCRUING",
  isAccrualTarget: false,
  isAvailable: false,
  isFreeSchedule: false,
  setPopupType: () => {},
  clearNotification: () => {},
})

interface Props {
  children: ReactNode
}

const WowMoneyProvider = ({ children }: Props) => {
  const { schedule } = useSchedule()
  const {
    account: { viewer },
  } = useAccount()
  const [popupType, setPopupType] = useState<WowMoneyPopupType>()

  const isScheduleEnd = ["END", undefined].includes(schedule?.scheduleStatus)
  const isFreeSchedule =
    !!schedule?.paymentInformation &&
    FREE_PAYMENT_INFORMATIONS.includes(schedule.paymentInformation)
  const isAvailable = !isScheduleEnd && isFreeSchedule
  const scheduleId = schedule?.id

  const checkScheduleEnded = useCallback(async () => {
    if (!scheduleId) return false

    const { data } = await scheduleApi.findBy(scheduleId)

    return data.scheduleStatus === "END"
  }, [scheduleId])

  const {
    cycleStartedAt,
    isAccrualTarget,
    cumulativePrice,
    notification,
    clearNotification,
  } = useWowMoney(setPopupType, {
    isAvailable,
    checkScheduleEnded,
  })

  let status: WowMoneyStatus = "ACCRUING"
  if (viewer?.role === "ANONYMOUS") status = "ANONYMOUS"
  else if (hasReachedMaxPrice(cumulativePrice)) status = "COMPLETED"
  else if (isScheduleEnd) status = "ENDED"

  const value = useMemo(
    () => ({
      cycleStartedAt,
      cumulativePrice,
      status,
      isAccrualTarget,
      isAvailable,
      isFreeSchedule,
      popupType,
      setPopupType,
      notification,
      clearNotification,
    }),
    [
      cycleStartedAt,
      cumulativePrice,
      status,
      isAccrualTarget,
      isAvailable,
      isFreeSchedule,
      popupType,
      notification,
      clearNotification,
    ],
  )

  return (
    <wowMoneyContext.Provider value={value}>
      {children}
    </wowMoneyContext.Provider>
  )
}

const useWowMoneyContext = () => useContext(wowMoneyContext)

export { WowMoneyProvider, wowMoneyContext, useWowMoneyContext }
